import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { LockKeyhole } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { EmptyState, Skeleton } from '@/components/ui/Feedback'
import { Container } from '@/components/layout/Container'
import { useI18n } from '@/i18n'
import { useOwner } from '@/hooks/useOwner'

/**
 * The gate on the studio. This site has ONE privileged account — the boutique
 * owner — so there is no role matrix here, just a single question answered in
 * `useOwner()`. Signed in with the wrong account gets its own message, because
 * "access denied" while clearly logged in is the most confusing screen there is.
 */
export function RequireOwner({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { loading, isOwner, isWrongAccount, user, signOut } = useOwner()

  if (loading) {
    return (
      <Container className="py-20 flex flex-col gap-5">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-72 w-full" />
      </Container>
    )
  }

  if (isWrongAccount) {
    return (
      <Container size="md">
        <EmptyState
          icon={<LockKeyhole size={22} strokeWidth={1.5} />}
          title={t('guard.wrongAccountTitle')}
          description={t('guard.wrongAccountBody', { email: user?.email ?? '' })}
          action={
            <Button
              variant="outline"
              onClick={async () => {
                await signOut()
                navigate('/signin?redirect=/studio')
              }}
            >
              {t('nav.signOut')}
            </Button>
          }
        />
      </Container>
    )
  }

  if (!isOwner) {
    const target = `${window.location.pathname}${window.location.search}`
    return (
      <Container size="md">
        <EmptyState
          icon={<LockKeyhole size={22} strokeWidth={1.5} />}
          title={t('guard.title')}
          description={t('guard.body')}
          action={
            <Button onClick={() => navigate(`/signin?redirect=${encodeURIComponent(target)}`)}>
              {t('guard.button')}
            </Button>
          }
        />
      </Container>
    )
  }

  return <>{children}</>
}
